import React, { useMemo, useState } from "react";
import Avatar from "../../../components/ui/Avatar";
import { uploadMyAvatar } from "../../../api/communityService";
import { useProfileHub } from "../ProfileHubContext";
import ProfileSectionCard from "../components/ProfileSectionCard";

function buildPreviewStyle(fileUrl, cropX, cropY, cropZoom) {
  return {
    backgroundImage: `url(${fileUrl})`,
    backgroundSize: `${cropZoom * 100}%`,
    backgroundPosition: `${cropX * 100}% ${cropY * 100}%`,
  };
}

export default function ProfileAvatarsSection() {
  const { profileIdentity, refreshProfile } = useProfileHub();
  const [selectedFile, setSelectedFile] = useState(null);
  const [cropX, setCropX] = useState(0.5);
  const [cropY, setCropY] = useState(0.5);
  const [cropZoom, setCropZoom] = useState(1.2);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const previewUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : null),
    [selectedFile]
  );

  const avatarStatusLabel =
    profileIdentity.avatar_status === "rejected"
      ? "Refuse"
      : "Actif";

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Choisis une image avant l'envoi.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setMessage("");

    try {
      await uploadMyAvatar({
        file: selectedFile,
        cropX,
        cropY,
        cropZoom,
      });
      await refreshProfile();
      setMessage("Avatar mis a jour.");
      setSelectedFile(null);
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "Impossible d'envoyer l'avatar."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-[0.8fr,1.2fr]">
        <ProfileSectionCard
          title="Avatar actuel"
          subtitle="Avatar personnel actuellement utilisé sur ton profil."
        >
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
            <Avatar
              src={profileIdentity.avatar?.image || null}
              name={profileIdentity.pseudo}
              size="xl"
              rounded="full"
            />
            <div>
              <div className="text-2xl font-semibold text-slate-900">
                {profileIdentity.pseudo}
              </div>
              <div className="mt-2 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-700">
                {avatarStatusLabel}
              </div>
              <div className="mt-3 max-w-xl text-sm leading-6 text-slate-500">
                Sans avatar personnel actif, le jeu affiche automatiquement un avatar
                par défaut basé sur des initiales.
              </div>
            </div>
          </div>
        </ProfileSectionCard>

        <ProfileSectionCard
          title="Uploader un avatar"
          subtitle="Cadrage carre simple avec apercu et application immediate."
        >
          <div className="space-y-5">
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {message ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {message}
              </div>
            ) : null}

            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                setSelectedFile(event.target.files?.[0] || null);
                setMessage("");
                setError("");
              }}
              className="block w-full text-sm text-slate-600"
            />

            <div className="grid gap-5 lg:grid-cols-[260px,1fr]">
              <div
                className="aspect-square rounded-[32px] border border-slate-200 bg-slate-100 bg-cover bg-no-repeat shadow-inner"
                style={
                  previewUrl
                    ? buildPreviewStyle(previewUrl, cropX, cropY, cropZoom)
                    : undefined
                }
              >
                {!previewUrl ? (
                  <div className="flex h-full items-center justify-center text-sm text-slate-500">
                    Aperçu carré
                  </div>
                ) : null}
              </div>

              <div className="space-y-4">
                <label className="block">
                  <div className="text-sm font-semibold text-slate-900">
                    Cadrage horizontal
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={cropX}
                    onChange={(event) => setCropX(Number(event.target.value))}
                    className="mt-2 w-full"
                  />
                </label>
                <label className="block">
                  <div className="text-sm font-semibold text-slate-900">
                    Cadrage vertical
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={cropY}
                    onChange={(event) => setCropY(Number(event.target.value))}
                    className="mt-2 w-full"
                  />
                </label>
                <label className="block">
                  <div className="text-sm font-semibold text-slate-900">
                    Zoom
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="2.5"
                    step="0.05"
                    value={cropZoom}
                    onChange={(event) =>
                      setCropZoom(Number(event.target.value))
                    }
                    className="mt-2 w-full"
                  />
                </label>

                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={isSubmitting}
                  className="rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? "Envoi..." : "Mettre à jour mon avatar"}
                </button>
              </div>
            </div>
          </div>
        </ProfileSectionCard>
      </div>
    </div>
  );
}
